import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Paperclip, CheckCircle2, Circle, Plus, MessageSquare, ListChecks } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_TIPS = {
  client: "Update your progress, share drafts, and respond to mentor questions in this stage.",
  student: "Complete required reading, submit notes and check off milestones.",
  mentor: "Review the client's progress, leave feedback, approve milestones, and request revisions.",
  tutor: "Share teaching materials, drop annotated feedback, and confirm understanding.",
  partner_institution: "Track student progress against institutional KPIs and add observations.",
  admin: "Audit the stage, intervene where needed, escalate to senior mentors.",
};

export default function StageDetailDrawer({ ideaId, stage, onClose, onRefresh, readOnly = false }) {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [todos, setTodos] = useState([]);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [comment, setComment] = useState("");
  const [att, setAtt] = useState({ name: "", url: "" });
  const [newMs, setNewMs] = useState({ title: "" });
  const [newTodo, setNewTodo] = useState({ title: "" });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("work");

  const load = async (opts = {}) => {
    // Only show the full-drawer loading state on the initial fetch. Subsequent
    // refreshes (triggered by task toggles, comment adds, etc.) update the
    // list in-place without a flash of empty state — this is what the
    // "no-flicker" ask is really after.
    if (opts.initial) setLoading(true);
    const [m, c, a, t] = await Promise.all([
      api.get(`/ideas/${ideaId}/milestones`, { params: { stage: stage.stage } }),
      api.get(`/ideas/${ideaId}/comments`, { params: { stage: stage.stage } }),
      api.get(`/ideas/${ideaId}/attachments`, { params: { stage: stage.stage } }),
      api.get(`/ideas/${ideaId}/todos`, { params: { stage: stage.stage } }),
    ]);
    setMilestones(m.data); setComments(c.data); setAttachments(a.data); setTodos(t.data);
    if (opts.initial) setLoading(false);
  };
  useEffect(() => { load({ initial: true }); /* eslint-disable-next-line */ }, [ideaId, stage.stage]);

  const toggleMs = async (ms) => {
    // Optimistic flip so the checkbox reacts instantly without waiting for
    // the API round-trip or the parent timeline refetch.
    setMilestones((arr) => arr.map((x) => x.id === ms.id ? { ...x, status: x.status === "completed" ? "pending" : "completed" } : x));
    try { await api.post(`/ideas/${ideaId}/milestones/${ms.id}/toggle`); onRefresh?.(); }
    catch (e) { toast.error("Failed to toggle milestone"); load(); }
  };
  const toggleTodo = async (td) => {
    setTodos((arr) => arr.map((x) => x.id === td.id ? { ...x, status: x.status === "completed" ? "pending" : "completed" } : x));
    try { await api.post(`/ideas/${ideaId}/todos/${td.id}/toggle`); onRefresh?.(); }
    catch (e) { toast.error("Failed to toggle todo"); load(); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/ideas/${ideaId}/comments`, { stage: stage.stage, body: comment }); setComment(""); await load(); onRefresh?.(); }
    catch (e) { toast.error("Failed"); }
  };
  const addAttachment = async () => {
    if (!att.name || !att.url) { toast.error("Both name and URL required"); return; }
    try { await api.post(`/ideas/${ideaId}/attachments`, { stage: stage.stage, name: att.name, url: att.url }); setAtt({ name: "", url: "" }); await load(); onRefresh?.(); }
    catch (e) { toast.error("Failed"); }
  };
  const addMs = async () => {
    if (!newMs.title.trim()) return;
    try { await api.post(`/ideas/${ideaId}/milestones`, { stage: stage.stage, title: newMs.title }); setNewMs({ title: "" }); await load(); onRefresh?.(); }
    catch (e) { toast.error("Failed"); }
  };
  const addTodo = async () => {
    if (!newTodo.title.trim()) return;
    try { await api.post(`/ideas/${ideaId}/todos`, { stage: stage.stage, title: newTodo.title }); setNewTodo({ title: "" }); await load(); onRefresh?.(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="stage-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full sm:max-w-2xl bg-white border-l border-slate-200 h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">
              STAGE · <span className={stage.state === "active" ? "text-kb-primary" : stage.state === "done" ? "text-emerald-600" : "text-kb-ink-light"}>{stage.state.toUpperCase()}</span>
            </div>
            <h2 className="font-serif text-2xl text-kb-ink mt-0.5">{stage.stage}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-kb-ink-muted hover:text-kb-primary" data-testid="stage-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-slate-200 bg-kb-subtle">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-1.5">FOR YOUR ROLE</div>
          <p className="text-sm text-kb-ink">{ROLE_TIPS[user?.role] || ROLE_TIPS.client}</p>
        </div>

        <div className="px-6 border-b border-slate-200 flex gap-1">
          {[{ k: "work", l: "Work" }, { k: "discussion", l: "Discussion" }, { k: "files", l: "Files" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-3 kb-mono text-[10px] uppercase tracking-wider border-b-2 -mb-px ${tab === t.k ? "border-kb-primary text-kb-primary" : "border-transparent text-kb-ink-muted hover:text-kb-primary"}`}
              data-testid={`stage-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        <div className="p-6 space-y-7">
          {tab === "work" && (
            <>
              <section>
                <div className="flex items-center justify-between">
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUB-PHASE MILESTONES · {milestones.filter(m => m.status === "completed").length}/{milestones.length}</div>
                  <span className="kb-mono text-xs text-kb-primary">{stage.progress_pct}%</span>
                </div>
                <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-kb-primary transition-all" style={{ width: `${stage.progress_pct}%` }} />
                </div>
                {loading ? <div className="kb-mono text-xs text-kb-ink-muted mt-4">Loading…</div> : (
                  <ul className="mt-4 space-y-1.5">
                    {milestones.map((m) => (
                      <li key={m.id} className="flex items-start gap-3 group">
                        <button onClick={() => !readOnly && toggleMs(m)} disabled={readOnly} className="mt-0.5 disabled:opacity-50" data-testid={`milestone-${m.id}-toggle`}>
                          {m.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300 group-hover:text-kb-primary" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${m.status === "completed" ? "text-kb-ink-muted line-through" : "text-kb-ink"}`}>{m.title}</div>
                          {m.completed_at && <div className="kb-mono text-[10px] text-emerald-600">DONE · {new Date(m.completed_at).toLocaleDateString()}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {!readOnly && (
                  <div className="mt-3 flex gap-2">
                    <input className="kb-input text-sm flex-1" placeholder="+ Add custom milestone…"
                      value={newMs.title} onChange={(e) => setNewMs({ title: e.target.value })} data-testid="stage-add-milestone-input" />
                    <button onClick={addMs} className="kb-btn-outline text-sm" data-testid="stage-add-milestone-btn"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </section>

              <section>
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2"><ListChecks className="w-3 h-3" /> TODOS · {todos.filter(t => t.status === "completed").length}/{todos.length}</div>
                {todos.length === 0 ? (
                  <div className="text-sm text-kb-ink-muted mt-2">No todos yet. Anyone (client, mentor, admin) can add items here.</div>
                ) : (
                  <ul className="mt-3 space-y-1.5">
                    {todos.map((t) => (
                      <li key={t.id} className="flex items-start gap-3 group" data-testid={`todo-${t.id}`}>
                        <button onClick={() => toggleTodo(t)} className="mt-0.5">
                          {t.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300 group-hover:text-kb-primary" />}
                        </button>
                        <div className="flex-1">
                          <div className={`text-sm ${t.status === "completed" ? "text-kb-ink-muted line-through" : "text-kb-ink"}`}>{t.title}</div>
                          <div className="kb-mono text-[10px] text-kb-ink-light">By {t.created_by_name}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {!readOnly && (
                  <div className="mt-3 flex gap-2">
                    <input className="kb-input text-sm flex-1" placeholder="+ Add a to-do…"
                      value={newTodo.title} onChange={(e) => setNewTodo({ title: e.target.value })} data-testid="stage-add-todo-input" />
                    <button onClick={addTodo} className="kb-btn-outline text-sm" data-testid="stage-add-todo-btn"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </section>
            </>
          )}

          {tab === "files" && (
            <section>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ATTACHMENTS · {attachments.length}</div>
              {attachments.length === 0 ? (
                <div className="text-sm text-kb-ink-muted mt-2">No attachments yet. Paste a link to PDF / Drive / GitHub below.</div>
              ) : (
                <ul className="mt-3 space-y-2">
                  {attachments.map((a) => {
                    const isPdf = a.url.toLowerCase().endsWith(".pdf") || a.mime === "application/pdf";
                    return (
                      <li key={a.id} className="border border-slate-200 rounded-sm overflow-hidden" data-testid={`stage-att-${a.id}`}>
                        <div className="px-3 py-2 flex items-center gap-3 bg-slate-50 border-b border-slate-200">
                          <Paperclip className="w-3.5 h-3.5 text-kb-primary" />
                          <a href={a.url} target="_blank" rel="noreferrer" className="text-sm text-kb-primary underline truncate flex-1">{a.name}</a>
                          <span className="kb-mono text-[10px] text-kb-ink-light">{a.user_name}</span>
                        </div>
                        {isPdf && <embed src={a.url} type="application/pdf" className="w-full" style={{ height: 320 }} />}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!readOnly && (
                <div className="mt-3 grid grid-cols-12 gap-2">
                  <input className="kb-input text-sm col-span-5" placeholder="Title" value={att.name}
                    onChange={(e) => setAtt({ ...att, name: e.target.value })} data-testid="stage-att-name-input" />
                  <input className="kb-input text-sm col-span-6" placeholder="URL" value={att.url}
                    onChange={(e) => setAtt({ ...att, url: e.target.value })} data-testid="stage-att-url-input" />
                  <button onClick={addAttachment} className="kb-btn-outline text-sm col-span-1" data-testid="stage-att-add-btn"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </section>
          )}

          {tab === "discussion" && (
            <section>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DISCUSSION · {comments.length}</div>
              {comments.length === 0 ? (
                <div className="text-sm text-kb-ink-muted mt-2">No comments yet — start a discussion with your mentor.</div>
              ) : (
                <ul className="mt-3 space-y-3">
                  {comments.map((c) => (
                    <li key={c.id} className="border-l-2 border-kb-primary/30 pl-4 py-1" data-testid={`stage-cmt-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-kb-ink">{c.user_name}</span>
                        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light bg-slate-100 px-1.5 py-0.5 rounded-sm">{c.user_role}</span>
                        <span className="kb-mono text-[10px] text-kb-ink-light">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-kb-ink mt-1 whitespace-pre-wrap">{c.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              {!readOnly && (
                <div className="mt-4 flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-kb-ink-light mt-2.5" />
                  <textarea rows={2} className="kb-input resize-none text-sm flex-1" placeholder="Add a comment…"
                    value={comment} onChange={(e) => setComment(e.target.value)} data-testid="stage-cmt-input" />
                  <button onClick={addComment} className="kb-btn-primary text-sm" data-testid="stage-cmt-send"><Send className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
